import { Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { FC, Fragment, useMemo, useState } from 'react';
import { berlin, C, Card, Chip, int, pct, TableWrap } from './ui';
import type { CampaignRow } from './types';

interface Group {
  key: string;
  title: string;
  subject: string | null;
  sendTimeUtc: string;
  beforeStart: boolean;
  rows: CampaignRow[];
}

/** Summen ueber Zeilen, deren Bericht vorliegt; Quoten = Summe Zaehler / Summe Empfaenger. */
function sums(rows: CampaignRow[]) {
  const mit = rows.filter((r) => r.emailsSent !== null);
  const s = (f: (r: CampaignRow) => number | null) => mit.reduce((a, r) => a + (f(r) ?? 0), 0);
  return {
    sent: mit.length ? s((r) => r.emailsSent) : null,
    real: s((r) => r.realUniqueOpens),
    clicks: s((r) => r.uniqueClicks),
    unsub: s((r) => r.unsubscribed),
    hard: s((r) => r.hardBounces),
    firstMail: rows.reduce((a, r) => a + r.firstMailLeads, 0),
    firstBillable: rows.reduce((a, r) => a + r.firstMailBillable, 0),
    firstOpen: rows.reduce((a, r) => a + r.firstMailOpen, 0),
  };
}

/** Kampagnenname ohne Nachversand-Zusaetze: Mailchimps „[Re-sent] …“ und unser „… Nachversand …“. */
export const baseTitle = (t: string) =>
  t.replace(/^\[re-?sent\]\s*/i, '').replace(/\s+Nachversand\b.*$/i, '').trim();
const NACHVERSAND_TAGE = 7;
const tageZwischen = (a: string, b: string) =>
  Math.abs(Date.parse(`${a.replace(' ', 'T')}Z`) - Date.parse(`${b.replace(' ', 'T')}Z`)) / 86_400_000;

export const CampaignsSection: FC<{ campaigns: CampaignRow[]; firstDeliveryUtc: string | null }> = ({ campaigns, firstDeliveryUtc }) => {
  const [scope, setScope] = useState<'since' | 'all'>('since');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [limit, setLimit] = useState(12);

  const groups = useMemo(() => {
    // Eine Kampagne = gleicher Name (ohne Nachversand-Zusatz) ueber alle Listen; Nachversaende innerhalb von
    // NACHVERSAND_TAGEN nach dem ersten Versand gehoeren dazu. Ein spaeterer gleichnamiger Versand wird eine
    // eigene Kampagne.
    const out: Group[] = [];
    const sortiert = [...campaigns]
      .filter((c) => !(scope === 'since' && c.beforeStart))
      .sort((a, b) => a.sendTimeUtc.localeCompare(b.sendTimeUtc));
    for (const c of sortiert) {
      const name = baseTitle(c.title ?? c.campaignId);
      let g = out.find((x) => x.title === name && tageZwischen(x.sendTimeUtc, c.sendTimeUtc) <= NACHVERSAND_TAGE);
      if (!g) {
        g = { key: `${name}|${c.sendTimeUtc}`, title: name, subject: c.subject, sendTimeUtc: c.sendTimeUtc, beforeStart: c.beforeStart, rows: [] };
        out.push(g);
      }
      g.rows.push(c);
      g.beforeStart = g.beforeStart && c.beforeStart;
    }
    return out.sort((a, b) => b.sendTimeUtc.localeCompare(a.sendTimeUtc));
  }, [campaigns, scope]);

  const toggle = (k: string) =>
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    <Card
      title="MSM-Versände"
      subtitle={
        <>
          Mailchimp-Berichte aller MSM-Versände. „Erstmail“ = gelieferte Leads, für die dieser Versand die erste MSM-Mail war.
          Echte Öffnungen ohne automatische Apple-Öffnungen.
        </>
      }
      action={
        <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && (setScope(v), setLimit(12))}>
          <ToggleButton value="since" sx={{ textTransform: 'none', px: 1.5 }}>
            Seit Lieferstart{firstDeliveryUtc ? ` (${berlin(firstDeliveryUtc, false)})` : ''}
          </ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>Alle 2026</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      {groups.length === 0 ? (
        <Typography sx={{ color: C.ink2, fontSize: 14 }}>Noch keine Versände in diesem Zeitraum.</Typography>
      ) : (
        <TableWrap>
          <table>
            <thead>
              <tr>
                <th>Versand</th>
                <th>Listen</th>
                <th>Empfänger</th>
                <th>Öffnungsrate</th>
                <th>Klickrate</th>
                <th>Abmelderate</th>
                <th>Bounces</th>
                <th>Erstmail</th>
                <th>davon abrechenbar</th>
              </tr>
            </thead>
            <tbody>
              {groups.slice(0, limit).map((g) => {
                const s = sums(g.rows);
                const isOpen = open.has(g.key);
                return (
                  <Fragment key={g.key}>
                    <tr style={{ background: isOpen ? '#f8fafc' : undefined }}>
                      <td style={{ whiteSpace: 'normal', minWidth: 220 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => toggle(g.key)} sx={{ mt: '-3px', ml: '-6px' }} aria-label="Listen aufklappen">
                            {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                          </IconButton>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ fontWeight: 600, color: C.ink }}>
                              {g.title} {g.beforeStart && <Chip color={C.ink2} bg="#eef1f5">vor Lieferstart</Chip>}
                            </Box>
                            <Box sx={{ fontSize: 12, color: C.ink3 }}>{berlin(g.sendTimeUtc)}</Box>
                            {g.subject && <Box sx={{ fontSize: 12, color: C.ink2, maxWidth: 380 }}>{g.subject}</Box>}
                          </Box>
                        </Box>
                      </td>
                      <td>{new Set(g.rows.map((r) => r.account)).size}</td>
                      <td>{int(s.sent)}</td>
                      <td>{pct(s.real, s.sent)}</td>
                      <td>{pct(s.clicks, s.sent, 2)}</td>
                      <td>{pct(s.unsub, s.sent, 2)}</td>
                      <td>{int(s.hard)}</td>
                      <td>{s.firstMail ? int(s.firstMail) : '–'}</td>
                      <td>
                        {s.firstMail ? (
                          s.firstOpen === s.firstMail ? (
                            <span style={{ color: C.ink3 }}>Frist läuft</span>
                          ) : (
                            <>
                              {int(s.firstBillable)} <span style={{ color: C.ink3 }}>{pct(s.firstBillable, s.firstMail - s.firstOpen)}</span>
                            </>
                          )
                        ) : (
                          '–'
                        )}
                      </td>
                    </tr>
                    {isOpen &&
                      [...g.rows]
                        .sort((a, b) => a.name.localeCompare(b.name) || a.sendTimeUtc.localeCompare(b.sendTimeUtc))
                        .map((r) => (
                          <tr key={`${r.account}/${r.campaignId}`} style={{ background: '#f8fafc', fontSize: 13 }}>
                            <td style={{ paddingLeft: 40, color: C.ink2 }}>
                              {r.name}
                              {tageZwischen(r.sendTimeUtc, g.sendTimeUtc) >= 0.5 && (
                                <span style={{ color: C.ink3 }}> · Nachversand {berlin(r.sendTimeUtc)}</span>
                              )}
                            </td>
                            <td />
                            <td>{int(r.emailsSent)}</td>
                            <td>{pct(r.realUniqueOpens, r.emailsSent)}</td>
                            <td>{pct(r.uniqueClicks, r.emailsSent, 2)}</td>
                            <td>{pct(r.unsubscribed, r.emailsSent, 2)}</td>
                            <td>{int(r.hardBounces)}</td>
                            <td>{r.firstMailLeads ? int(r.firstMailLeads) : '–'}</td>
                            <td>{r.firstMailLeads ? (r.firstMailOpen === r.firstMailLeads ? 'Frist läuft' : int(r.firstMailBillable)) : '–'}</td>
                          </tr>
                        ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
      {groups.length > limit && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button size="small" onClick={() => setLimit((l) => l + 12)} sx={{ textTransform: 'none', color: C.brand }}>
            Weitere Versände anzeigen ({groups.length - limit})
          </Button>
        </Box>
      )}
    </Card>
  );
};
